<template>
  <div
    class="border-ontick-steel-300 dark:border-ontick-carbon-500 dark:bg-ontick-graphite-500 relative transform cursor-pointer space-y-3 overflow-hidden rounded-xl border-2 bg-gray-50 py-5 pb-2 transition-all duration-200 hover:-translate-y-1 hover:scale-105"
  >
    <div class="flex justify-between px-4">
      <div class="w-5/6">
        <p class="text-ontick-steel-400 truncate text-sm dark:text-gray-400">Portfólio</p>

        <dt class="flex items-center space-x-2">
          <img
            v-if="product.partner_badge || partnerInfo"
            :src="product.partner_badge || partnerInfo.partner_badge"
            :alt="product.partner_name || partnerInfo.partner_name"
            class="h-5 w-5"
            :content="product.partner_name || partnerInfo.partner_name"
            v-tippy="{ arrow: true, theme: 'green' }"
          />
          <tier-icon-component
            :tier="strategy.strategy_tier"
            v-else-if="strategy.strategy_tier && strategy.strategy_tier !== 'NO_TIER' && $route.name !== 'Analistas XP'"
          />

          <ontick-broker-logo-component :broker="product.partner_institution" v-if="$route.name === 'Analistas XP'" />

          <h3
            class="text-ontick-carbon-900 truncate text-2xl font-semibold dark:text-gray-300"
            :content="strategy.strategy_name"
            v-tippy="{ arrow: true, theme: 'green' }"
          >
            {{ strategy.strategy_name || strategy }}
          </h3>
        </dt>
      </div>

      <marketplace-add-component
        v-if="addButton"
        :status="strategy.marketplace_status"
        :label="textTooltip(strategy.marketplace_status)"
        :disableAdd="disableAdd"
        @click.native.prevent.stop="subscription()"
      />
    </div>

    <div class="chart-line-marketplace">
      <div class="flex space-x-2 px-4">
        <div
          :content="`Risco: ${strategy.strategy_risk}`"
          v-tippy="{ arrow: true, theme: 'green', placement: 'bottom' }"
          :class="
            strategy.strategy_risk == 'BAIXO'
              ? 'border-ontick-green-600'
              : strategy.strategy_risk == 'MEDIO'
                ? 'border-ontick-yellow-600'
                : 'border-ontick-red-600 '
          "
          class="rounded border px-3 py-1 text-xs"
        >
          <span
            :class="
              strategy.strategy_risk == 'BAIXO'
                ? 'text-ontick-green-600'
                : strategy.strategy_risk == 'MEDIO'
                  ? 'text-ontick-yellow-600'
                  : 'text-ontick-red-600 '
            "
          >
            {{ strategy.strategy_risk.replaceAll("_", " ").replaceAll("E", "É") }}
          </span>
        </div>
        <div
          :content="`Modo de operação: ${strategy.strategy_style.replaceAll('_', ' ')}`"
          v-tippy="{ arrow: true, theme: 'green', placement: 'bottom' }"
          :class="colorStatus(strategy.strategy_style)"
          class="whitespace-nowrap rounded px-3 py-1 text-xs"
        >
          {{ strategy.strategy_style.replaceAll("_", " ") }}
        </div>
      </div>

      <div class="h-[128px] w-full">
        <img
          :src="`https://ontick-estrategias.s3.sa-east-1.amazonaws.com/${strategy.strategy_id}.png`"
          :alt="`Curva de capital da estratégia ${strategy.strategy_name}`"
          loading="lazy"
          class="h-full w-full"
          :class="[{ 'contrast-200 saturate-200': config.ui_theme === 'LIGHT' }]"
        />
        <div
          class="absolute top-1/2 right-0 flex h-[120px] w-[120px] -translate-y-1/2 transform items-center justify-center"
        >
          <VChart :option="chartOptions()" class="chart" autoresize :init-options="{ height: 120, width: 120 }" />
        </div>
      </div>
    </div>
    <!-- <div v-else-if="product.product_type == 'PORTFOLIO'">
      <CardPortfolio :strategy="strategy" />
    </div> -->

    <div class="flex items-center sm:col-span-2 sm:flex">
      <div class="col-span-2 grid flex-grow grid-cols-4 gap-1 px-1 py-1 text-center">
        <!-- VOLUME -->
        <div content="Volume mínimo" v-tippy="{ arrow: true, theme: 'green' }">
          <dd class="text-ontick-carbon-900 text-xs font-semibold lining-nums dark:font-medium dark:text-gray-300">
            {{ strategy.min_volume }}
          </dd>
          <dt class="text-ontick-steel-400 truncate text-xs font-normal dark:text-gray-400">Volume</dt>
        </div>

        <!-- CAPITAL -->
        <div content="Capital recomendado" v-tippy="{ arrow: true, theme: 'green' }">
          <dd class="text-ontick-carbon-900 text-xs font-semibold lining-nums dark:font-medium dark:text-gray-300">
            R${{ strategy.min_capital }}
          </dd>
          <dt class="text-ontick-steel-400 truncate text-xs font-normal dark:text-gray-400">Capital</dt>
        </div>

        <!-- RETORNO -->
        <div content="Retorno acumulado" v-tippy="{ arrow: true, theme: 'green' }">
          <dd
            class="text-ontick-green-700 dark:text-ontick-green-600 text-xs font-semibold lining-nums dark:font-medium"
          >
            {{ strategy.tr_perc_return.toFixed(2) }}%
          </dd>
          <dt class="text-ontick-steel-400 truncate text-xs font-normal dark:text-gray-400">Retorno</dt>
        </div>

        <!-- PERDA MÁXIMA -->
        <div
          :content="strategy.max_loss === 0 ? 'Estratégia sem limite de perda configurado' : 'Perda máxima diária'"
          v-tippy="{
            arrow: true,
            theme: strategy.max_loss === 0 ? 'red' : 'green',
          }"
        >
          <dd
            class="text-ontick-red-700 dark:text-ontick-red-600 flex items-center justify-center text-xs font-semibold lining-nums dark:font-medium"
          >
            <span v-if="strategy.max_loss"> R${{ strategy.max_loss }} </span>
            <span v-else class="flex items-center space-x-1" content="">
              <span> N/D </span>
              <i v-html="$icons.red_alert_sm"></i>
            </span>
          </dd>
          <dt class="text-ontick-steel-400 truncate text-xs font-normal dark:text-gray-400">Perda máx.</dt>
        </div>
      </div>

      <!-- BOTÃO DETALHES -->
      <button
        :class="visualization ? 'hidden' : ''"
        class="pr-4 text-gray-400"
        content="Ver detalhes"
        v-tippy="{ arrow: true, theme: 'green' }"
      >
        <i v-html="$icons.kebab"></i>
      </button>
    </div>

    <!-- <div
      @click="transition()"
      class="
        bg-red-500 bg-opacity-50
        w-full
        h-full
        form
        circ
        inset-0
        absolute
        z-50
        play
      "
    ></div> -->
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { PieChart } from "echarts/charts";
import { TitleComponent, TooltipComponent, LegendComponent } from "echarts/components";
import VChart, { THEME_KEY } from "vue-echarts";
import OntickBrokerLogoComponent from "../../../../../shared/components/basic/ontick-broker-logo.component.vue";

use([CanvasRenderer, PieChart, TitleComponent, TooltipComponent, LegendComponent]);

export default {
  name: "cardMarketplace",

  components: {
    VChart,
    Favorite: () =>
      import("@/app/modules/marketplace/components/products/favorite/marketplace-favorite-heart.component.vue"),
    MarketplaceAddComponent: () =>
      import("@/app/modules/marketplace/components/products/card/marketplace-add.component.vue"),

    CardPortfolio: () => import("./marketplace-card.portfolio.component.vue"),
    TierIconComponent: () => import("../../../../../shared/components/basic/ontick-tier-icon.component.vue"),
    OntickBrokerLogoComponent,
  },

  props: {
    product: { type: Object },
    strategy: { type: Object },
    favorite: { type: Boolean },
    visualization: { type: Boolean },
    partnerInfo: { type: Object },
    addButton: { type: Boolean, default: true },
    disableAdd: { type: Boolean, default: false },
  },

  data() {
    return {
      chart: null,
      dark: true,
    };
  },

  computed: {
    ...mapState({
      accountTypes: (state) => state.accountTypes,
      config: (state) => state.config,
    }),
    ...mapGetters({
      getSelectedAccount: "accountTypes/getSelectedAccount",
      getColors: "investment/getColors",
    }),
  },

  methods: {
    chartOptions() {
      return {
        tooltip: {
          // show: false,
          appendToBody: true,
          trigger: "item",
        },
        // legend: {
        //   top: "5%",
        //   left: "center",
        // },
        series: [
          {
            name: "Access From",
            type: "pie",
            radius: ["60%", "100%"],
            center: ["60%", "60%"],
            height: 100,
            width: 100,
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 2,
              color: (value) => {
                return this.getColors[value.dataIndex];
              },
            },
            label: {
              show: false,
              position: "center",
            },
            emphasis: {
              show: false,
              // label: {
              //   show: true,
              //   fontSize: 40,
              //   fontWeight: "bold",
              // },
            },
            labelLine: {
              show: false,
            },
            data: [
              { value: 1048, name: "Search Engine" },
              { value: 735, name: "Direct" },
              { value: 580, name: "Email" },
              { value: 484, name: "Union Ads" },
              { value: 300, name: "Video Ads" },
            ],
          },
        ],
      };
    },
    translate(val) {
      switch (val) {
        case "STRATEGY":
          return "ESTRATÉGIA";
        case "TRADE_FOLLOW":
          return "COPY TRADE";
        case "SIGNAL":
          return "SINAL";
        default:
          return null;
      }
    },
    abbreviate(val) {
      if (val === "STRATEGY") return "E";
      if (val === "TRADE_FOLLOW") return "CT";
      let arr = val.split("_");
      let initials = arr.map((item) => item.charAt(0));
      return initials.join("").toUpperCase();
    },
    transition() {
      var tl = new TimelineLite({}); //pause default

      //collapse menu
      tl.set(".play", { top: "5%", left: "7%", ease: Quart.easeIn })
        // .set("fast", { opacity: 0, ease: Power4.easeIn }, "label--0")
        // .set("rewind", { opacity: 0, ease: Power4.easeIn }, "label--0")

        .to(".circ", 0.33, { height: 435, width: 435, ease: Quint.easeInOut, force3D: true }, "label--2")

        .to(
          ".form",
          0.2,
          {
            transformOrigin: "0% 100%",
            top: 228,
            height: 172,
            ease: Quint.easeInOut,
            force3D: true,
          },
          "label--2"
        )

        .to(".circ", 0.36, { top: -132, right: -18, ease: Power4.easeInOut, force3D: true }, "label--2")

        .to(
          ".play",
          0.4,
          {
            top: "90%",
            left: "90%",
            height: 15,
            width: 15,
            borderWidth: 0,
            scaleX: 1,
            boxShadow: "inset -5px 0px 0px black, inset 5px 0px 0px black",
            ease: Power4.easeInOut,
            force3D: true,
          },
          "label--2"
        );
    },
    async subscription() {
      if (["MARKETPLACE", "EXTENSION_ONLY"].includes(this.product.product_listed)) {
        this.trackSubscription();
        try {
          const res = await this.$api.post(this.$endpoint.ADD_MARKETPLACE_STRATEGY, {
            product_id: this.product.product_id,
            strategy_id: this.strategy.strategy_id,
            account_id: this.getSelectedAccount.account_id,
          });
          await this.$swal({
            title: "Sucesso",
            text: this.$filters.capitalize(res.data.msg),
            icon: "success",
            iconHtml: this.$icons.swal_success,
            timer: 3000,
            timerProgressBar: true,
          });
          this.$router.push({ path: "/investments" });
        } catch (err) {
          console.error(err);
          this.$emit("upgrade-needed", {
            strategy: this.strategy,
            error: err.data,
          });
        }
      } else if (["EXTENSION", "NOT_LISTED"].includes(this.product.product_listed)) {
        if (this.getSelectedAccount.account_type === "CONTEST") {
          this.trackSubscription();
          try {
            const res = await this.$api.post(this.$endpoint.ADD_EXTENSION_STRATEGY, {
              product_id: this.product.product_id,
              strategy_id: this.strategy.strategy_id,
              account_id: this.getSelectedAccount.account_id,
            });
            await this.$swal({
              title: "Sucesso",
              text: this.$filters.capitalize(res.data.msg),
              icon: "success",
              iconHtml: this.$icons.swal_success,
              timer: 3000,
              timerProgressBar: true,
            });
            this.$router.push({ path: "/investments" });
          } catch (err) {
            console.error(err);
            if (typeof err.data === "string") {
              this.$swal("Erro", this.$filters.capitalize(err.data), "error");
              return;
            }
            if (err.data.action === "REDIRECT_SUBSCRIPTION") {
              this.$router.push({
                name: "Detalhes da extensão",
                params: {
                  partner_name: this.product.partner_name,
                  extension_group: this.product.extension_group,
                },
              });
              return;
            }
            this.$swal("Erro", this.$filters.capitalize(err.data.msg), "error");
          }
        } else if (this.strategy.marketplace_status === "EXTENSION") {
          this.$router.push({
            name: "Detalhes da extensão",
            params: {
              partner_name: this.product.partner_name,
              extension_group: this.product.extension_group,
            },
          });
        } else {
          this.trackSubscription();
          try {
            const res = await this.$api.post(this.$endpoint.ADD_EXTENSION_STRATEGY, {
              product_id: this.product.product_id,
              strategy_id: this.strategy.strategy_id,
              account_id: this.getSelectedAccount.account_id,
            });
            await this.$swal({
              title: "Sucesso",
              text: this.$filters.capitalize(res.data.msg),
              icon: "success",
              iconHtml: this.$icons.swal_success,
              timer: 3000,
              timerProgressBar: true,
            });
            this.$router.push({ path: "/investments" });
          } catch (err) {
            console.error(err);
            if (typeof err.data === "string") {
              this.$swal("Erro", this.$filters.capitalize(err.data), "error");
              return;
            }
            if (err.data.action === "REDIRECT_SUBSCRIPTION") {
              this.$router.push({
                name: "Detalhes da extensão",
                params: {
                  partner_name: this.product.partner_name,
                  extension_group: this.product.extension_group,
                },
              });
              return;
            }
            this.$swal("Erro", this.$filters.capitalize(err.data.msg), "error");
          }
        }
      }
    },
    confirmPaymentFree() {
      let params = {
        product_id: parseInt(this.product.product_id),
        strategy_id: parseInt(this.product.strategy_id),
        license_price_id: this.product.license_price_id,
      };

      this.$api.post(this.$endpoint.FREE_PAYMENTS, params).then((res) => {
        this.$swal({
          icon: "success",
          iconHtml: this.$icons.swal_success,
          title: "Produto adquirido com sucesso",
          timer: 3000,
          timerProgressBar: true,
        }).then(() => {
          this.$router.push({ path: "/investments" });
        });
      });
    },
    trackSubscription() {
      this.$gtm.trackEvent({
        event: "interaction", // Event type [default = 'interaction'] (Optional)
        category: "Assinatura",
        action: "click",
        label: "Assinatura iniciada",
        value: `(${this.accountTypes.selectedAccount.broker_name} - ${this.accountTypes.selectedAccount.account_number}) ${this.strategy.strategy_id} - ${this.strategy.strategy_name}`,
        noninteraction: false, // Optional
      });
    },
    percentageConvert(value) {
      return parseFloat(value).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
      });
    },
    colorStatus(value) {
      let color = "";
      if (value == "DAY_TRADE") {
        color = `border border-ontick-orange-500 text-ontick-orange-500 `;
      } else if (value == "SWING_TRADE") {
        color = `border border-ontick-blue-900 text-ontick-blue-500 `;
      }
      return color;
    },
    colorType(value) {
      const types = {
        // TRADE_FOLLOW: " bg-purple-800 dark:text-purple-300 text-gray-50",
        TRADE_FOLLOW: " border border-purple-800 dark:text-purple-300 text-gray-50",
        LONG_SHORT: "bg-ontick-teal-600 dark:text-ontick-teal-100 text-gray-50",
        PORTFOLIO: "border border-gray-300 text-gray-300",
        // PORTFOLIO: "bg-ontick-prussian-100 text-ontick-blue-300",
      };
      return types[value];
    },
    textTooltip(status) {
      const tooltips = {
        ADDED: "Você já adicionou este investimento",
        HIDDEN: "Este investimento está em sua lista de arquivados",
        default: "Adicione agora esta estratégia aos seus investimentos",
      };
      return tooltips[status] || tooltips.default;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/app/modules/marketplace/pages/marketplace/marketplace.component.scss";

.img-chart {
  animation: fadeIn 1.5s ease-in-out 1;
}
@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
